import { createNanoEvents } from 'nanoevents';
import { KeyboardInput, GamepadButtonInput, AllInputCodes } from './types/inputs.types';
import { gamepadButtonsMap, getAxisEvent, inputToActionsMap } from './config/inputMap';
import { GamepadAxisEvent, GamepadButtonEvent, GamepadListener } from 'gamepad.js';
import { LatestInputInfo } from './types/context.types';
import { DeviceType } from './types/device.types';
import { setupDomNavigation } from './config/dom-navigation';
import { GameContext, InputActions } from '@game-libs/models';

export class InputsMediator {
  public currentContext: GameContext = GameContext.IN_GAME;
  public latestInputInfo: LatestInputInfo | null = null;

  private gamepad = new GamepadListener({ analog: false, deadZone: 0.5 });
  public inputEvents = createNanoEvents();
  public releaseEvents = createNanoEvents();

  public inputValues = Object.values(InputActions).reduce(
    (result, item) => {
      result[item] = 0;

      return result;
    },
    {} as Record<InputActions, number>,
  );

  constructor() {
    this.setupInputListeners();
    this.setupMenuNavigation();

    // Binding so that `this` isn't undefined when called from outside class
    this.onceInputEvent = this.onceInputEvent.bind(this);
  }

  public onceInputEvent(inputAction: InputActions, cb: (...args: any) => void) {
    const unbind = this.inputEvents.on(inputAction, () => {
      cb();
      unbind();
    });

    return unbind;
  }

  destroy() {
    this.gamepad.stop();
    try {
      this.gamepad.removeGamepad(0);
    } catch { }
    this.inputEvents.events = {};
  }

  public pauseInputs() {
    this.gamepad.stop();
  }

  public resumeInputs() {
    this.gamepad.start();
  }

  public setupMenuNavigation() {
    setupDomNavigation(this.inputEvents);
  }

  private setupInputListeners() {
    this.gamepad.start();

    this.gamepad.on('gamepad:button', (event: GamepadButtonEvent) => {
      const { value, button } = event.detail;

      const code = gamepadButtonsMap[button] as GamepadButtonInput;

      const InputActions = inputToActionsMap.gamepadButton[code];
      this.updateGameInputValues(InputActions, value, code);

      this.updateLatestInputInfo(DeviceType.Gamepad, code, InputActions);
    });

    this.gamepad.on('gamepad:axis', (event: GamepadAxisEvent) => {
      const axisEvent = getAxisEvent(event);

      if (!axisEvent) return;

      const InputActions = inputToActionsMap.gamepadStick[axisEvent];

      this.updateGameInputValues(InputActions, event.detail.value, axisEvent);

      this.updateLatestInputInfo(DeviceType.Gamepad, axisEvent, InputActions);
    });

    document.addEventListener(
      'keydown',
      (event: KeyboardEvent) => {

        if (event.repeat) {
          return; // Ignore repeated keydown events
        }

        const code = event.code as KeyboardInput;
        const InputActions = inputToActionsMap.keyboard[code];
        this.updateGameInputValues(InputActions, 1, code);

        this.updateLatestInputInfo(DeviceType.Keyboard, code, InputActions);

        if (code === 'ArrowDown' || code === 'ArrowUp') {
          event.preventDefault();
        }
      },
      false,
    );

    document.addEventListener(
      'keyup',
      (event: KeyboardEvent) => {
        const code = event.code as KeyboardInput;
        const InputActions = inputToActionsMap.keyboard[code];
        this.updateGameInputValues(InputActions, 0, code);
      },
      false,
    );
  }

  public simulateInput(actions: InputActions[], inputValue: number) {
    this.updateGameInputValues(actions, inputValue);
  }

  private updateGameInputValues(actions: InputActions[], inputValue: number, code?: string) {
    if (!actions) return;

    actions?.map((event: InputActions) => (this.inputValues[event] = Math.abs(inputValue)));

    // Forward actions to external clients
    // if (inputValue !== 0) {
    this.emitGameActionEvents(actions, inputValue, code);
    // }
  }

  private emitGameActionEvents(actions: InputActions[], inputValue: number, code?: string) {
    if (inputValue === 0) {
      actions?.map((action: InputActions) => this.releaseEvents.emit(action, { value: inputValue, code }));
    } else {
      actions?.map((action: InputActions) => this.inputEvents.emit(action, { value: inputValue, code }));
    }
  }

  private updateLatestInputInfo(device: DeviceType, key: AllInputCodes, actions: InputActions[]) {
    this.latestInputInfo = {
      device,
      key,
      actions,
    };
  }

  public movementVector(normalize = true): {
    x: number;
    y: number;
  } {
    const horizontalVector = this.inputValues[InputActions.MOVE_RIGHT] - this.inputValues[InputActions.MOVE_LEFT];
    const verticalVector = this.inputValues[InputActions.MOVE_DOWN] - this.inputValues[InputActions.MOVE_UP];

    let x = horizontalVector ?? 0;
    let y = verticalVector ?? 0;

    if (normalize) {
      const length = Math.sqrt(x * x + y * y);

      if (length !== 0) {
        // Normalize vector
        x /= length;
        y /= length;
      }
    }
    return { x, y };
  }
}
